import React, { useState, useRef, useEffect } from "react";
import "./styles.sass";
import { useQuestionBankViewHandler } from "./handler";
import { useApi } from "../../hooks/use-api";
import { useQuestionBankHandler } from "../question-bank/handler";
import {
  LatexRenderer,
  normalizeLatexString,
  containsLatexPattern,
  cleanLatexString,
} from "../../components/latex-renderer";
import { getChapterDisplayLabel } from "../../utils/chapter-mapping";
import { ChoosePopup } from "./choose-popup";
import { ImportPopup } from "./import-popup";
import { ImportedQuestionsModal } from "../question-bank/imported-questions-modal";
import { SuccessModal } from "../question-bank/success-modal";

export const formatQuestionTypeLabel = (raw) => {
  if (!raw || raw === "All" || raw === "all") return "All Types";
  const trimmed = String(raw).trim();
  const upper = trimmed.toUpperCase();

  const dictionary = {
    MCQ: "MCQ",
    SINGLE_CORRECT: "Single Correct MCQ",
    MULTIPLE_CORRECT: "Multiple Correct MCQ",
    ASSERTION_REASON: "Assertion & Reason",
    ASSERTION_REASONING: "Assertion & Reason",
    ASSERTION_AND_REASONING: "Assertion & Reason",
    IMAGE_BASED_ASSERTION_AND_REASONING: "Image-Based Assertion & Reason",
    IMAGE_BASED_ASSERTION_REASON: "Image-Based Assertion & Reason",
    IMAGE_BASED_ASSERTION_REASONING: "Image-Based Assertion & Reason",
    IMAGE_BASED_MCQ: "Image-Based MCQ",
    DIAGRAM_BASED_MCQ: "Diagram-Based MCQ",
    DIAGRAM_MCQ: "Diagram-Based MCQ",
    IMAGE_BASED_STATEMENT: "Image-Based Statement",
    IMAGE_BASED_STATEMENT_TYPE: "Image-Based Statement",
    IMAGE_BASED_STATEMENT_QUESTION: "Image-Based Statement",
    MATCH_THE_FOLLOWING: "Match the Following",
    MATCHING: "Match the Following",
    STATEMENT_TYPE: "Statement Type",
    STATEMENT_QUESTION: "Statement Question",
    STATEMENT: "Statement Question",
    CONCEPTUAL: "Conceptual",
    NUMERICAL: "Numerical",
    NUMERIC: "Numerical",
    INTEGER: "Integer",
    PARAGRAPH: "Paragraph",
    SHORT: "Short Answer",
  };

  const normalizedKey = upper.replace(/\s+/g, "_");
  if (dictionary[normalizedKey]) return dictionary[normalizedKey];
  if (dictionary[upper]) return dictionary[upper];

  return trimmed
    .replace(/[_-]+/g, " ")
    .split(" ")
    .filter(Boolean)
    .map((w) => {
      const lower = w.toLowerCase();
      if (lower === "mcq") return "MCQ";
      if (lower === "and") return "&";
      if (lower === "the") return "the";
      if (lower === "of") return "of";
      return w.charAt(0).toUpperCase() + lower.slice(1);
    })
    .join(" ");
};

export const CustomSelectDropdown = ({
  value,
  onChange,
  options = [],
  formatLabel,
  placeholder = "Select...",
  disabled = false,
  disabledMessage = "",
  emptyMessage = "No options available",
  loading = false,
  loadingMessage = "Loading...",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const getLabel = (val) => {
    if (formatLabel) return formatLabel(val);
    return val;
  };

  const currentLabel =
    value !== undefined && value !== null && value !== ""
      ? getLabel(value)
      : (placeholder || "Select...");

  return (
    <div
      ref={dropdownRef}
      className={`qb_custom_select_wrapper ${isOpen ? "is_open" : ""} ${disabled ? "is_disabled" : ""}`}
    >
      <button
        type="button"
        className={`qb_custom_select_trigger ${disabled ? "disabled_btn" : ""}`}
        onClick={() => {
          if (!disabled) {
            setIsOpen((prev) => !prev);
          } else if (disabledMessage) {
            setIsOpen((prev) => !prev);
          }
        }}
        title={disabled ? (disabledMessage || placeholder) : currentLabel}
      >
        <span className="selected_text">{disabled ? (placeholder || disabledMessage) : currentLabel}</span>
        {loading ? (
          <div className="select_mini_spinner" />
        ) : (
          <svg
            className={`select_chevron ${isOpen ? "rotate" : ""}`}
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
          >
            <polyline points="6 9 12 15 18 9"></polyline>
          </svg>
        )}
      </button>

      {isOpen && (
        <div className="qb_custom_select_dropdown_menu">
          {disabled && disabledMessage ? (
            <div className="qb_custom_select_message disabled_notice">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#6366F1" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="16" x2="12" y2="12" />
                <line x1="12" y1="8" x2="12.01" y2="8" />
              </svg>
              <span>{disabledMessage}</span>
            </div>
          ) : loading ? (
            <div className="qb_custom_select_message loading_notice">
              <div className="select_menu_spinner" />
              <span>{loadingMessage}</span>
            </div>
          ) : options.length === 0 ? (
            <div className="qb_custom_select_message empty_notice">
              <span>{emptyMessage}</span>
            </div>
          ) : (
            options.map((opt) => {
              const optVal = typeof opt === "object" ? opt.value : opt;
              const optLabel = typeof opt === "object" ? opt.label : getLabel(opt);
              const isSelected = value === optVal;

              return (
                <div
                  key={optVal}
                  className={`qb_custom_select_option ${isSelected ? "selected" : ""}`}
                  onClick={() => {
                    onChange(optVal);
                    setIsOpen(false);
                  }}
                  title={optLabel}
                >
                  <span className="option_label">{optLabel}</span>
                  {isSelected && (
                    <svg
                      className="selected_check"
                      width="13"
                      height="13"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#612EF7"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};

const getPageNumbers = (currentPage, totalPages) => {
  if (!totalPages || totalPages <= 0) return [1];
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }
  if (currentPage <= 4) {
    return [1, 2, 3, 4, 5, "...", totalPages];
  }
  if (currentPage >= totalPages - 3) {
    return [1, "...", totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
  }
  return [1, "...", currentPage - 1, currentPage, currentPage + 1, "...", totalPages];
};

const renderHighlightedString = (text, highlight) => {
  if (!highlight || !highlight.trim() || !text) return text;
  try {
    const escaped = highlight.trim().replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const regex = new RegExp(`(${escaped})`, "gi");
    const parts = String(text).split(regex);
    if (parts.length <= 1) return text;
    return parts.map((part, i) =>
      regex.test(part) ? (
        <mark key={i} className="qb_search_highlight">
          {part}
        </mark>
      ) : (
        part
      )
    );
  } catch {
    return text;
  }
};

const handleImageError = (e) => {
  let currentSrc = e.target.src || "";
  const attempt = Number(e.target.dataset.retryAttempt || "0");
  if (attempt === 0) {
    e.target.dataset.retryAttempt = "1";
    currentSrc = currentSrc.replace(/(%22|%27|["',\\\/])+$/gi, "");
    if (currentSrc.includes("visal1chatbotvm.southindia.cloudapp.azure.com")) {
      let newSrc = currentSrc;
      if (newSrc.includes(":8001")) {
        newSrc = newSrc
          .replace("http://", "https://")
          .replace(":8001/generated_files/", "/secure-api/generated_files/")
          .replace(":8001/question_assets/", "/secure-api/question_assets/")
          .replace(":8001/", "/secure-api/")
          .replace(
            "visal1chatbotvm.southindia.cloudapp.azure.com:8001",
            "visal1chatbotvm.southindia.cloudapp.azure.com"
          );
      } else {
        newSrc = newSrc
          .replace("https://", "http://")
          .replace(
            "visal1chatbotvm.southindia.cloudapp.azure.com",
            "visal1chatbotvm.southindia.cloudapp.azure.com:8001"
          )
          .replace(":8001/secure-api/", ":8001/");
      }
      if (newSrc !== currentSrc) {
        e.target.src = newSrc;
        return;
      }
    }
  }
};

export const QuestionBankView = () => {
  const {
    viewMode,
    selectedSource,
    setSelectedSource,
    handleOpenSource,
    handleOpenPapers,
    handleBackToCards,
    loadingQuestions,
    loadingPapers,
    reloadQuestions,
    // Pagination
    currentPage,
    pageSize,
    totalQuestionsCount,
    totalPages,
    startIndex,
    endIndex,
    handlePageChange,
    handlePageSizeChange,
    // Filters
    searchQuery,
    setSearchQuery,
    selectedClass,
    handleClassChange,
    selectedExam,
    handleExamChange,
    selectedChapter,
    handleChapterChange,
    selectedConcept,
    handleConceptChange,
    selectedType,
    handleTypeChange,
    classOptions,
    examOptions,
    chapterOptions,
    conceptOptions,
    typeOptions,
    loadingChapters,
    loadingConcepts,
    isClassSelected,
    isChapterSelected,
    hasActiveFilters,
    resetAllFilters,
    // Data & Selection
    filteredQuestions,
    filteredPapers,
    selectedQuestionIds,
    toggleQuestionSelection,
    handleSelectAllVisible,
    previewPaper,
    previewModalOpen,
    setPreviewModalOpen,
    handleOpenPaperPreview,
    expandedSolutions,
    toggleSolution,
    handleCopyQuestion,
    updateQuestionInState,
  } = useQuestionBankViewHandler();

  const api = useApi();
  const {
    editQuestion,
    uploadQuestionImage,
    deleteQuestionImage,
  } = useQuestionBankHandler(api);

  const [editingQuestion, setEditingQuestion] = useState(null);
  const [editPromptText, setEditPromptText] = useState("");
  const [editOptionA, setEditOptionA] = useState("");
  const [editOptionB, setEditOptionB] = useState("");
  const [editOptionC, setEditOptionC] = useState("");
  const [editOptionD, setEditOptionD] = useState("");
  const [editOptionE, setEditOptionE] = useState("");
  const [editCorrectAnswer, setEditCorrectAnswer] = useState("A");
  const [editSolution, setEditSolution] = useState("");
  const [editStemImage, setEditStemImage] = useState(null);
  const [editOptionImages, setEditOptionImages] = useState({
    A: null,
    B: null,
    C: null,
    D: null,
    E: null,
  });
  const [uploadingTarget, setUploadingTarget] = useState(null);

  const handleOpenEdit = (q) => {
    setEditingQuestion(q);
    const prompt = q.question_text || q.text || "";
    setEditPromptText(cleanLatexString(prompt));

    const getOptText = (letter) => {
      const fromArray = q.options?.find((o) => o.id === letter)?.text;
      if (typeof fromArray === "string" && fromArray) return fromArray;
      const direct = q[`option${letter}`];
      if (typeof direct === "string" && direct) return direct;
      return "";
    };

    const getOptImg = (letter) => {
      const fromArray = q.options?.find((o) => o.id === letter)?.image;
      if (fromArray) return fromArray;
      const fromObj =
        q.optionImages?.[letter] ||
        q.option_images?.[letter] ||
        q.option_images?.[letter.toLowerCase()];
      return fromObj || null;
    };

    setEditOptionA(cleanLatexString(getOptText("A")));
    setEditOptionB(cleanLatexString(getOptText("B")));
    setEditOptionC(cleanLatexString(getOptText("C")));
    setEditOptionD(cleanLatexString(getOptText("D")));
    setEditOptionE(cleanLatexString(getOptText("E")));

    const isMcq =
      (q.type || "").toLowerCase().includes("mcq") ||
      (q.options && q.options.length > 0);
    const rawAnswer = String(q.correct_answer || "").trim().toUpperCase();
    let normCorrect = "A";
    if (rawAnswer === "B" || rawAnswer === "OPTION B" || rawAnswer === "OPTION_B")
      normCorrect = "B";
    else if (rawAnswer === "C" || rawAnswer === "OPTION C" || rawAnswer === "OPTION_C")
      normCorrect = "C";
    else if (rawAnswer === "D" || rawAnswer === "OPTION D" || rawAnswer === "OPTION_D")
      normCorrect = "D";
    else if (rawAnswer === "E" || rawAnswer === "OPTION E" || rawAnswer === "OPTION_E")
      normCorrect = "E";
    else if (q.options) {
      const correctOpt = q.options.find((o) => o.is_correct);
      if (correctOpt?.id) normCorrect = correctOpt.id.toUpperCase();
    }

    setEditCorrectAnswer(isMcq ? normCorrect : q.correct_answer || "");
    setEditSolution(cleanLatexString(q.explanation || q.solution || ""));
    setEditStemImage(
      (q.image_urls && q.image_urls[0]) ||
      (q.imageUrls && q.imageUrls[0]) ||
      null
    );
    setEditOptionImages({
      A: getOptImg("A"),
      B: getOptImg("B"),
      C: getOptImg("C"),
      D: getOptImg("D"),
      E: getOptImg("E"),
    });
  };

  const handleStemImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !editingQuestion) return;
    setUploadingTarget("stem");
    const currentSource = (selectedSource === "ai" || editingQuestion?.source === "ai") ? "ai" : "bank";
    const res = await uploadQuestionImage(editingQuestion.id, "stem", file, currentSource);
    setUploadingTarget(null);
    if (res && (res.status === "success" || res.url || res.image_urls)) {
      const newUrl = res.url || (res.image_urls && res.image_urls[0]) || null;
      const updatedStemUrls = res.image_urls || (newUrl ? [newUrl] : []);
      setEditStemImage(newUrl);
      setEditingQuestion((prev) =>
        prev
          ? { ...prev, imageUrls: updatedStemUrls, image_urls: updatedStemUrls }
          : null
      );
      if (updateQuestionInState) {
        updateQuestionInState(editingQuestion.id, (q) => ({
          ...q,
          imageUrls: updatedStemUrls,
          image_urls: updatedStemUrls,
        }));
      }
    }
  };

  const handleStemImageDelete = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (e && e.stopPropagation) e.stopPropagation();
    if (!editingQuestion) return;
    setUploadingTarget("stem");
    const currentUrl = editStemImage || "";
    const currentSource = (selectedSource === "ai" || editingQuestion?.source === "ai") ? "ai" : "bank";
    const res = await deleteQuestionImage(editingQuestion.id, "stem", currentUrl, currentSource);
    setUploadingTarget(null);
    if (res) {
      const updatedStemUrls = res.image_urls || [];
      setEditStemImage(null);
      setEditingQuestion((prev) =>
        prev
          ? {
            ...prev,
            imageUrls: updatedStemUrls,
            image_urls: updatedStemUrls,
            image_url: null,
            image: null,
          }
          : null
      );
      if (updateQuestionInState) {
        updateQuestionInState(editingQuestion.id, (q) => ({
          ...q,
          imageUrls: updatedStemUrls,
          image_urls: updatedStemUrls,
          image_url: null,
          image: null,
        }));
      }
    }
  };

  const handleOptionImageUpload = async (optionKey, e) => {
    const file = e.target.files?.[0];
    if (!file || !editingQuestion) return;
    const targetLabel = optionKey.toLowerCase();
    setUploadingTarget(optionKey);
    const currentSource = (selectedSource === "ai" || editingQuestion?.source === "ai") ? "ai" : "bank";
    const res = await uploadQuestionImage(editingQuestion.id, targetLabel, file, currentSource);
    setUploadingTarget(null);
    if (res && (res.status === "success" || res.url || res.option_images)) {
      const newUrl =
        res.url ||
        res.option_images?.[targetLabel] ||
        res.option_images?.[optionKey] ||
        null;
      setEditOptionImages((prev) => ({ ...prev, [optionKey]: newUrl }));
      setEditingQuestion((prev) => {
        if (!prev) return null;
        const updatedOptImgs = { ...(prev.optionImages || {}), [optionKey]: newUrl };
        return { ...prev, optionImages: updatedOptImgs, option_images: updatedOptImgs };
      });
      if (updateQuestionInState) {
        updateQuestionInState(editingQuestion.id, (q) => {
          const updatedOptImgs = { ...(q.optionImages || {}), [optionKey]: newUrl };
          const updatedOptions = (q.options || []).map((opt) =>
            opt.id === optionKey ? { ...opt, image: newUrl } : opt
          );
          return {
            ...q,
            options: updatedOptions,
            optionImages: updatedOptImgs,
            option_images: updatedOptImgs,
          };
        });
      }
    }
  };

  const handleOptionImageDelete = async (optionKey, e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (e && e.stopPropagation) e.stopPropagation();
    if (!editingQuestion) return;
    const targetLabel = optionKey.toLowerCase();
    setUploadingTarget(optionKey);
    const currentUrl = editOptionImages?.[optionKey] || "";
    const currentSource = (selectedSource === "ai" || editingQuestion?.source === "ai") ? "ai" : "bank";
    const res = await deleteQuestionImage(editingQuestion.id, targetLabel, currentUrl, currentSource);
    setUploadingTarget(null);
    if (res) {
      setEditOptionImages((prev) => ({ ...prev, [optionKey]: null }));
      setEditingQuestion((prev) => {
        if (!prev) return null;
        const updatedOptImgs = { ...(prev.optionImages || {}), [optionKey]: null };
        return { ...prev, optionImages: updatedOptImgs, option_images: updatedOptImgs };
      });
      if (updateQuestionInState) {
        updateQuestionInState(editingQuestion.id, (q) => {
          const updatedOptImgs = { ...(q.optionImages || {}), [optionKey]: null };
          const updatedOptions = (q.options || []).map((opt) =>
            opt.id === optionKey ? { ...opt, image: null } : opt
          );
          return {
            ...q,
            options: updatedOptions,
            optionImages: updatedOptImgs,
            option_images: updatedOptImgs,
          };
        });
      }
    }
  };

  const formatCanonicalOptions = (optTexts, optImgs, isNumeric, originalQ) => {
    if (isNumeric) {
      return { options: {}, optionImages: {}, hasOptE: false };
    }

    const cleanedA = optTexts?.a || "";
    const cleanedB = optTexts?.b || "";
    const cleanedC = optTexts?.c || "";
    const cleanedD = optTexts?.d || "";
    const cleanedE = optTexts?.e || "";

    const imgA = optImgs?.a || optImgs?.A || null;
    const imgB = optImgs?.b || optImgs?.B || null;
    const imgC = optImgs?.c || optImgs?.C || null;
    const imgD = optImgs?.d || optImgs?.D || null;
    const imgE = optImgs?.e || optImgs?.E || null;

    const hasOptE = Boolean(cleanedE.trim() || imgE);
    const origHasD = Boolean(
      originalQ?.optionD?.trim() ||
      originalQ?.optionImages?.D ||
      originalQ?.options?.some((o) => o.id === "D")
    );
    const hasOptD = Boolean(cleanedD.trim() || imgD || hasOptE || origHasD);
    const origHasC = Boolean(
      originalQ?.optionC?.trim() ||
      originalQ?.optionImages?.C ||
      originalQ?.options?.some((o) => o.id === "C")
    );
    const hasOptC = Boolean(cleanedC.trim() || imgC || hasOptD || origHasC);

    const activeKeys = ["a", "b"];
    if (hasOptC) activeKeys.push("c");
    if (hasOptD) activeKeys.push("d");
    if (hasOptE) activeKeys.push("e");

    const textMap = { a: cleanedA, b: cleanedB, c: cleanedC, d: cleanedD, e: cleanedE };
    const imgMap = { a: imgA, b: imgB, c: imgC, d: imgD, e: imgE };

    const canonicalOptions = {};
    const canonicalOptionImages = {};

    activeKeys.forEach((key) => {
      canonicalOptions[key] = textMap[key] || "";
      canonicalOptionImages[key] = imgMap[key] || null;
    });

    return {
      options: canonicalOptions,
      optionImages: canonicalOptionImages,
      hasOptE,
    };
  };

  const formatCanonicalCorrectAnswer = (ans, isNumeric) => {
    if (isNumeric) return String(ans || "").trim();
    const raw = String(ans || "").trim();
    const upper = raw.toUpperCase();
    if (upper === "A" || upper === "OPTION A" || upper === "OPTION_A" || upper === "OPTIONA")
      return "a";
    if (upper === "B" || upper === "OPTION B" || upper === "OPTION_B" || upper === "OPTIONB")
      return "b";
    if (upper === "C" || upper === "OPTION C" || upper === "OPTION_C" || upper === "OPTIONC")
      return "c";
    if (upper === "D" || upper === "OPTION D" || upper === "OPTION_D" || upper === "OPTIOND")
      return "d";
    if (upper === "E" || upper === "OPTION E" || upper === "OPTION_E" || upper === "OPTIONE")
      return "e";
    return raw.toLowerCase();
  };

  const handleEditSave = async () => {
    if (!editingQuestion) return;
    const questionId = editingQuestion.id;

    const cleanedPrompt = normalizeLatexString(editPromptText);
    const cleanedOptA = normalizeLatexString(editOptionA);
    const cleanedOptB = normalizeLatexString(editOptionB);
    const cleanedOptC = normalizeLatexString(editOptionC);
    const cleanedOptD = normalizeLatexString(editOptionD);
    const cleanedOptE = normalizeLatexString(editOptionE);
    const cleanedSol = normalizeLatexString(editSolution);

    const currentOptImgs = editOptionImages || editingQuestion.optionImages || {};

    const isNumeric =
      (editingQuestion.type || "").toUpperCase() === "NUMERIC" ||
      (editingQuestion.type || "").toUpperCase() === "SHORT" ||
      (editingQuestion.type || "").toUpperCase() === "NUMERICAL";

    const optTexts = {
      a: cleanedOptA,
      b: cleanedOptB,
      c: cleanedOptC,
      d: cleanedOptD,
      e: cleanedOptE,
    };

    const { options: canonicalOptions, optionImages: canonicalOptionImages, hasOptE } =
      formatCanonicalOptions(
        optTexts,
        currentOptImgs,
        isNumeric,
        editingQuestion
      );

    const currentStemUrls = editStemImage
      ? [editStemImage]
      : editingQuestion.image_urls || editingQuestion.imageUrls || [];
    const canonicalAnswer = formatCanonicalCorrectAnswer(editCorrectAnswer, isNumeric);

    const payload = {
      question_text: cleanedPrompt,
      options: canonicalOptions,
      option_images: canonicalOptionImages,
      image_urls: currentStemUrls,
      correct_answer: canonicalAnswer,
      solution: cleanedSol,
    };

    const isAi = selectedSource === "ai" || editingQuestion?.source === "ai";
    const res = await editQuestion(questionId, payload, isAi);
    const resOptImgs =
      res?.question?.option_images || res?.option_images || canonicalOptionImages;
    const updatedOptImgs = {
      A: resOptImgs.A || resOptImgs.a || currentOptImgs.A || currentOptImgs.a || null,
      B: resOptImgs.B || resOptImgs.b || currentOptImgs.B || currentOptImgs.b || null,
      C: resOptImgs.C || resOptImgs.c || currentOptImgs.C || currentOptImgs.c || null,
      D: resOptImgs.D || resOptImgs.d || currentOptImgs.D || currentOptImgs.d || null,
      E: hasOptE
        ? resOptImgs.E || resOptImgs.e || currentOptImgs.E || currentOptImgs.e || null
        : null,
    };

    const hasAnyOptText = Boolean(cleanedOptA || cleanedOptB || cleanedOptC || cleanedOptD || (hasOptE && cleanedOptE));
    const hasAnyOptImg = Boolean(updatedOptImgs.A || updatedOptImgs.B || updatedOptImgs.C || updatedOptImgs.D || (hasOptE && updatedOptImgs.E));
    const isValidStructure = isNumeric ? Boolean(cleanedPrompt && cleanedPrompt.trim()) : (hasAnyOptText || hasAnyOptImg);

    const apiIsValid = (res && res.is_valid !== undefined) ? Boolean(res.is_valid) : (res?.question?.is_valid !== undefined ? Boolean(res.question.is_valid) : true);
    const newIsValid = apiIsValid && isValidStructure;

    if (updateQuestionInState) {
      updateQuestionInState(questionId, (oldQ) => {
        const updatedOptions = [];
        const optKeys = ["A", "B", "C", "D"];
        if (hasOptE) optKeys.push("E");
        const textMap = {
          A: cleanedOptA,
          B: cleanedOptB,
          C: cleanedOptC,
          D: cleanedOptD,
          E: cleanedOptE,
        };
        const upperCorrect = (canonicalAnswer || "A").toUpperCase();

        optKeys.forEach((key) => {
          const optText = textMap[key] || "";
          const optImg = updatedOptImgs[key] || null;
          if (
            optText ||
            optImg ||
            (oldQ.options && oldQ.options.some((o) => o.id === key))
          ) {
            updatedOptions.push({
              id: key,
              text: optText,
              image: optImg,
              is_correct: upperCorrect === key || upperCorrect.includes(key),
            });
          }
        });

        return {
          ...oldQ,
          question_text: cleanedPrompt,
          text: cleanedPrompt,
          optionA: cleanedOptA,
          optionB: cleanedOptB,
          optionC: cleanedOptC,
          optionD: cleanedOptD,
          optionE: hasOptE ? cleanedOptE : "",
          options: updatedOptions.length > 0 ? updatedOptions : oldQ.options,
          optionImages: updatedOptImgs,
          option_images: updatedOptImgs,
          image_urls: currentStemUrls,
          imageUrls: currentStemUrls,
          correct_answer: canonicalAnswer,
          explanation: cleanedSol,
          solution: cleanedSol,
          isValid: newIsValid,
          validationError: newIsValid ? null : (res?.detail || res?.message || oldQ.validationError),
        };
      });
    }

    setEditingQuestion(null);
    setEditPromptText("");
    setEditOptionA("");
    setEditOptionB("");
    setEditOptionC("");
    setEditOptionD("");
    setEditOptionE("");
    setEditSolution("");
  };

  // Image Zoom Lightbox Modal State
  const [zoomModalImage, setZoomModalImage] = useState(null);
  const [zoomScale, setZoomScale] = useState(1.5);

  const handleOpenZoom = (e, fallbackSrc, title) => {
    if (e && e.stopPropagation) e.stopPropagation();
    let finalSrc = fallbackSrc;
    const parentBox = e?.currentTarget?.closest(".img_preview_box");
    const activeImg = parentBox?.querySelector("img");
    if (activeImg && (activeImg.currentSrc || activeImg.src)) {
      finalSrc = activeImg.currentSrc || activeImg.src;
    }
    setZoomScale(1.5);
    setZoomModalImage({ src: finalSrc, title });
  };

  // Choose Questions Popup State
  const [showChooseModal, setShowChooseModal] = useState(false);
  // Import Questions Popup State
  const [showImportModal, setShowImportModal] = useState(false);
  const [showImportedQuestions, setShowImportedQuestions] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [activeTestName, setActiveTestName] = useState("NEET Biology Mock Test–01");
  const [isImportFlow, setIsImportFlow] = useState(false);
  const [activeLevel, setActiveLevel] = useState("");

  const handleProceedChoose = (sourceKey) => {
    setShowChooseModal(false);
    handleOpenSource(sourceKey);
  };

  const handleUploadSuccess = (rows, metadata = {}) => {
    if (metadata?.testName) {
      setActiveTestName(metadata.testName);
    }
    if (metadata?.level) {
      setActiveLevel(metadata.level);
    }
    setShowImportModal(false);
    setIsImportFlow(true);
    setShowImportedQuestions(true);
    if (reloadQuestions) {
      reloadQuestions();
    }
  };

  const handleCompleteQuestionPaper = () => {
    setShowImportedQuestions(false);
    setShowSuccess(true);
  };

  return (
    <div className="qb_view_container">
      {/* ────────────────────────────────────────────────────────────
          MODE 1: OVERVIEW CARDS MODE (Default)
          ──────────────────────────────────────────────────────────── */}
      {viewMode === "cards" && (
        <div className="qb_overview_section">
          <div className="qb_header_section">
            <div className="qb_title_group">
              <h1>Question Bank</h1>
              <p>Manage and organize your question banks</p>
            </div>
          </div>

          <h2 className="qb_subtitle">Choose how you want to view questions</h2>

          {/* Main 2 Cards Grid */}
          <div className="qb_main_cards_grid">
            {/* Card 1: View from Question Bank */}
            <div
              className="qb_create_card green_theme"
              onClick={() => setShowChooseModal(true)}
            >
              <div className="qb_card_banner green_bg">
                <div className="qb_sheet_graphic">
                  <div className="sheet_bar green"></div>
                  <div className="sheet_line"></div>
                  <div className="sheet_line"></div>
                  <div className="sheet_line short"></div>
                  <div className="sheet_floating_icon green">
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
                      <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
                    </svg>
                  </div>
                </div>
              </div>

              <div className="qb_card_content">
                <span className="qb_badge_tag green_badge">QUESTION BANK</span>
                <h3>View questions from the question bank</h3>
                <p className="card_desc">
                  Browse and view questions directly from the structured question bank.
                </p>

                <div className="divider"></div>

                <ul className="checklist">
                  <li>
                    <div className="check_icon green">
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="3"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                    </div>
                    View questions from the question bank
                  </li>
                  <li>
                    <div className="check_icon green">
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="3"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                    </div>
                    Organize by question type
                  </li>
                  <li>
                    <div className="check_icon green">
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="3"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                    </div>
                    View and filter questions
                  </li>
                  <li>
                    <div className="check_icon green">
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="3"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                    </div>
                    Assign to test
                  </li>
                  <li>
                    <div className="check_icon green">
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="3"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                    </div>
                    Publish questions to students
                  </li>
                </ul>

                <button
                  type="button"
                  className="card_action_btn green_btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowChooseModal(true);
                  }}
                >
                  View Questions →
                </button>
              </div>
            </div>

            {/* Card 2: AI Generated Questions */}
            <div
              className="qb_create_card purple_theme"
              onClick={() => setShowImportModal(true)}
            >
              <div className="qb_card_banner purple_bg">
                <div className="qb_sheet_graphic">
                  <div className="sheet_bar purple"></div>
                  <div className="sheet_line"></div>
                  <div className="sheet_line"></div>
                  <div className="sheet_line short"></div>
                  <div className="sheet_floating_icon purple">
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z" />
                    </svg>
                  </div>
                </div>
              </div>

              <div className="qb_card_content">
                <span className="qb_badge_tag purple_badge">BRING YOUR OWN</span>
                <h3>Bring your own questions</h3>
                <p className="card_desc">
                  Upload your set of questions to choose from.
                </p>

                <div className="divider"></div>

                <ul className="checklist">
                  <li>
                    <div className="check_icon purple">
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="3"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                    </div>
                    Upload questions from your PDF or Excel
                  </li>
                  <li>
                    <div className="check_icon purple">
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="3"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                    </div>
                    Organize by question type
                  </li>
                  <li>
                    <div className="check_icon purple">
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="3"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                    </div>
                    Select questions
                  </li>
                  <li>
                    <div className="check_icon purple">
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="3"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                    </div>
                    Assign to test
                  </li>
                  <li>
                    <div className="check_icon purple">
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="3"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                    </div>
                    Publish questions to students
                  </li>
                </ul>

                <button
                  type="button"
                  className="card_action_btn purple_btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowImportModal(true);
                  }}
                >
                  Upload Questions →
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ────────────────────────────────────────────────────────────
          MODE 2: MOCK QUESTIONS EXPLORER VIEW (Shown on card click)
          ──────────────────────────────────────────────────────────── */}
      {viewMode === "questions" && (
        <div className="qb_questions_view_section">
          {/* Header Navigation */}
          <div className="qb_questions_top_bar">
            <button
              type="button"
              className="back_to_cards_btn"
              onClick={handleBackToCards}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="19" y1="12" x2="5" y2="12" />
                <polyline points="12 19 5 12 12 5" />
              </svg>
              Back to Options
            </button>
          </div>

          {/* Section Heading Banner */}
          <div className="qb_section_banner">
            <div className="banner_info">
              {selectedSource === "bank" && (
                <>
                  <span className="badge_pill green">QUESTION BANK POOL</span>
                  <h2>Standard Question Bank Repository</h2>
                  <p>Browse structured questions organized by class, exam, chapters, concepts, and question types.</p>
                </>
              )}
              {selectedSource === "ai" && (
                <>
                  <span className="badge_pill purple">AI CURATED • 45,000+ QUESTIONS</span>
                  <h2>AI Generated & Verified Questions</h2>
                  <p>Explore high-yield NEET and JEE questions dynamically generated and verified by AI.</p>
                </>
              )}
              {selectedSource === "all" && (
                <>
                  <span className="badge_pill blue">ALL QUESTION SOURCES</span>
                  <h2>Combined Question Repository</h2>
                  <p>Access all available questions across Question Bank and AI generated pools.</p>
                </>
              )}
            </div>
          </div>

          {/* ────────────────────────────────────────────────────────────
              FILTERS TOOLBAR (Search, Class, Exam, Chapters, Concepts, Question Type)
              ──────────────────────────────────────────────────────────── */}
          <div className="qb_filters_card">
            {/* Top Search Bar */}
            <div className="filter_search_row">
              <div className="search_input_wrap">
                <svg
                  className="search_icon"
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#64748B"
                  strokeWidth="2.2"
                >
                  <circle cx="11" cy="11" r="8"></circle>
                  <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                </svg>
                <input
                  type="text"
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="off"
                  spellCheck="false"
                  placeholder="Search questions by keyword, chapter, formula, or concept..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                {searchQuery && (
                  <button
                    type="button"
                    className="clear_search_btn"
                    onClick={() => setSearchQuery("")}
                    title="Clear search"
                  >
                    ×
                  </button>
                )}
              </div>

              <div className="filter_actions_group">
                <button
                  type="button"
                  className="reload_questions_btn"
                  onClick={reloadQuestions}
                  title="Reload questions from database"
                >
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M21.5 2v6h-6M21.34 15.57a10 10 0 1 1-.57-8.38l5.67-5.67" />
                  </svg>
                  Reload Questions
                </button>

                {hasActiveFilters && (
                  <button
                    type="button"
                    className="reset_filters_btn"
                    onClick={resetAllFilters}
                  >
                    <svg
                      width="13"
                      height="13"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                    >
                      <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
                      <path d="M3 3v5h5" />
                    </svg>
                    Reset Filters
                  </button>
                )}
              </div>
            </div>

            {/* Bottom Row: 5 Clean Filters (Class, Exam, Chapters, Concepts, Question Type) */}
            <div className="filters_grid_row">
              {/* 1. Exam Filter */}
              <div className="filter_control_pill">
                <span className="control_label">Exam</span>
                <CustomSelectDropdown
                  value={selectedExam}
                  onChange={handleExamChange}
                  options={examOptions}
                  formatLabel={(val) => (val === "All" ? "All Exams" : val)}
                />
              </div>

              {/* 2. Class Filter */}
              <div className="filter_control_pill">
                <span className="control_label">Class</span>
                <CustomSelectDropdown
                  value={selectedClass}
                  onChange={handleClassChange}
                  options={classOptions}
                  formatLabel={(val) => (val === "All" ? "All Classes" : val)}
                />
              </div>

              {/* 3. Chapters Filter */}
              <div className="filter_control_pill wide_pill">
                <span className="control_label">Chapters</span>
                <CustomSelectDropdown
                  value={selectedChapter}
                  onChange={handleChapterChange}
                  options={chapterOptions}
                  formatLabel={getChapterDisplayLabel}
                  disabled={!isClassSelected}
                  disabledMessage="Please select a Class first to view chapters"
                  placeholder={!isClassSelected ? "Select Class First" : (selectedChapter === "All" ? "All Chapters" : getChapterDisplayLabel(selectedChapter))}
                  loading={loadingChapters}
                  loadingMessage="Loading chapters..."
                  emptyMessage={isClassSelected ? "No chapters found for this class" : "Please select a Class first"}
                />
              </div>

              {/* 4. Concepts Filter */}
              <div className="filter_control_pill wide_pill">
                <span className="control_label">Concepts</span>
                <CustomSelectDropdown
                  value={selectedConcept}
                  onChange={handleConceptChange}
                  options={conceptOptions}
                  formatLabel={(val) =>
                    val === "All"
                      ? "All Concepts"
                      : String(val).replace(/^Concept\s*:\s*:?\s*/i, "").trim()
                  }
                  disabled={!isChapterSelected}
                  disabledMessage={
                    !isClassSelected
                      ? "Please select a Class first"
                      : "Please select a Chapter first to view concepts"
                  }
                  placeholder={
                    !isClassSelected
                      ? "Select Class First"
                      : !isChapterSelected
                        ? "Select Chapter First"
                        : (selectedConcept === "All" ? "All Concepts" : String(selectedConcept).replace(/^Concept\s*:\s*:?\s*/i, "").trim())
                  }
                  loading={loadingConcepts}
                  loadingMessage="Loading concepts..."
                  emptyMessage={
                    !isClassSelected
                      ? "Please select a Class first"
                      : !isChapterSelected
                        ? "Please select a Chapter first"
                        : "No concepts found for this chapter"
                  }
                />
              </div>

              {/* 5. Question Type Filter */}
              <div className="filter_control_pill wide_pill">
                <span className="control_label">Question Type</span>
                <CustomSelectDropdown
                  value={selectedType}
                  onChange={handleTypeChange}
                  options={typeOptions}
                  formatLabel={formatQuestionTypeLabel}
                />
              </div>
            </div>
          </div>

          {/* Results Summary Bar */}
          <div className="qb_results_bar">
            <div className="results_info">
              <div className="count_pill_badge">
                <span className="showing_label">Showing</span>
                <span className="range_highlight">
                  {totalQuestionsCount > 0
                    ? `${startIndex} – ${endIndex}`
                    : filteredQuestions.length}
                </span>
                <span className="questions_label">questions</span>
              </div>

            </div>
          </div>

          {/* Question Cards List */}
          <div className="qb_question_cards_list">
            {loadingQuestions ? (
              <div className="empty_state loading_ai_state">
                <div className="empty_icon">⚡</div>
                <h3>Fetching Questions...</h3>
                <p>Loading questions matching your selected filters from database.</p>
              </div>
            ) : filteredQuestions.length === 0 ? (
              <div className="empty_state">
                <div className="empty_icon">🔍</div>
                <h3>No matching questions found</h3>
                <p>Try clearing or adjusting your filter criteria.</p>
                <button
                  type="button"
                  className="empty_reset_btn"
                  onClick={resetAllFilters}
                >
                  Reset All Filters
                </button>
              </div>
            ) : (
              filteredQuestions.map((q, idx) => {
                const isSolutionExpanded = !!expandedSolutions[q.id];

                return (
                  <div key={q.id} className={`question_render_card ${q.isValid === false ? "has_error" : ""}`}>
                    {/* Question Card Top Meta */}
                    <div className="q_header_row">
                      <div className="q_left_meta">
                        <span className="q_num_badge">Q{idx + 1}</span>

                        {q.isValid === false ? (
                          <div className="error_banner_row">
                            <span className="error_tag">Error</span>
                            <span className="error_msg">
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#DC2626" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                                <line x1="12" y1="9" x2="12" y2="13" />
                                <line x1="12" y1="17" x2="12.01" y2="17" />
                              </svg>
                              {q.validationError || "This question could not be extracted correctly and need manual review"}
                            </span>
                          </div>
                        ) : (
                          <>
                            <span className="q_tag class_tag">{q.class_name || "Class 11"}</span>
                            <span className="q_tag exam_tag">{q.exam}</span>
                            <span className="q_tag subject_tag">{q.subject}</span>
                            <span className="q_tag type_tag">{q.type}</span>
                          </>
                        )}
                      </div>

                      <div className="q_right_actions">
                        <button
                          type="button"
                          className="edit_btn"
                          title="Edit Question"
                          onClick={() => handleOpenEdit(q)}
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#612EF7" strokeWidth="2">
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                          </svg>
                          Edit
                        </button>
                      </div>
                    </div>

                    {/* Chapter & Concept hierarchy info */}
                    <div className="q_topic_info">
                      <span className="info_segment">
                        <span className="qb_topic_label">Chapter:</span>
                        <span className="qb_topic_value">
                          {renderHighlightedString(
                            getChapterDisplayLabel(q.chapter),
                            searchQuery
                          )}
                        </span>
                      </span>
                      <span className="sep">•</span>
                      <span className="info_segment">
                        <span className="qb_topic_label">Concept:</span>
                        <span className="qb_topic_value">
                          {renderHighlightedString(
                            String(q.concept || "").replace(/^Concept\s*:\s*:?\s*/i, ""),
                            searchQuery
                          )}
                        </span>
                      </span>
                    </div>

                    {/* Question Statement (with LaTeX support and search highlight) */}
                    {(q.question_text || (q.image_urls && q.image_urls.length > 0)) && (
                      <div className="q_statement">
                        {q.question_text ? (
                          <LatexRenderer text={q.question_text} highlight={searchQuery} />
                        ) : null}

                        {q.image_urls && q.image_urls.length > 0 && (
                          <div className="q_statement_images">
                            {q.image_urls.map((imgUrl, imgIdx) => (
                              <div key={imgIdx} className="q_image_wrapper">
                                <div className="img_preview_box">
                                  <img
                                    src={imgUrl}
                                    alt={`Question diagram ${imgIdx + 1}`}
                                    className="q_stem_img"
                                    loading="lazy"
                                    onError={handleImageError}
                                    onClick={(e) =>
                                      handleOpenZoom(
                                        e,
                                        imgUrl,
                                        `Question Q${idx + 1} Diagram`
                                      )
                                    }
                                  />
                                  <button
                                    type="button"
                                    className="img_zoom_action_btn"
                                    title="Click to Zoom In"
                                    onClick={(e) =>
                                      handleOpenZoom(
                                        e,
                                        imgUrl,
                                        `Question Q${idx + 1} Diagram`
                                      )
                                    }
                                  >
                                    <svg
                                      width="13"
                                      height="13"
                                      viewBox="0 0 24 24"
                                      fill="none"
                                      stroke="currentColor"
                                      strokeWidth="2.5"
                                    >
                                      <circle cx="11" cy="11" r="8"></circle>
                                      <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                                      <line x1="11" y1="8" x2="11" y2="14"></line>
                                      <line x1="8" y1="11" x2="14" y2="11"></line>
                                    </svg>
                                    <span>Zoom In</span>
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Options Grid */}
                    {q.options && q.options.length > 0 && (
                      <div className="q_options_list">
                        {q.options.map((opt) => (
                          <div
                            key={opt.id}
                            className={`q_option_item ${opt.is_correct ? "correct_option" : ""
                              } ${opt.image ? "has_option_img" : ""}`}
                          >
                            <span className="opt_letter">{opt.id}</span>
                            <div className="opt_content_wrap">
                              {opt.text ? (
                                <div className="opt_text">
                                  <LatexRenderer text={opt.text} inline highlight={searchQuery} />
                                </div>
                              ) : null}
                              {opt.image && (
                                <div className="opt_image_wrap">
                                  <div className="img_preview_box opt_box">
                                    <img
                                      src={opt.image}
                                      alt={`Option ${opt.id}`}
                                      className="opt_render_img"
                                      loading="lazy"
                                      onError={handleImageError}
                                      onClick={(e) =>
                                        handleOpenZoom(
                                          e,
                                          opt.image,
                                          `Question Q${idx + 1} - Option ${opt.id}`
                                        )
                                      }
                                    />
                                    <button
                                      type="button"
                                      className="img_zoom_action_btn opt_zoom_btn"
                                      title="Click to Zoom In"
                                      onClick={(e) =>
                                        handleOpenZoom(
                                          e,
                                          opt.image,
                                          `Question Q${idx + 1} - Option ${opt.id}`
                                        )
                                      }
                                    >
                                      <svg
                                        width="11"
                                        height="11"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2.5"
                                      >
                                        <circle cx="11" cy="11" r="8"></circle>
                                        <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                                        <line x1="11" y1="8" x2="11" y2="14"></line>
                                        <line x1="8" y1="11" x2="14" y2="11"></line>
                                      </svg>
                                      <span>Zoom In</span>
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                            {opt.is_correct && (
                              <span className="correct_badge">
                                <svg
                                  width="12"
                                  height="12"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="3.5"
                                >
                                  <polyline points="20 6 9 17 4 12" />
                                </svg>
                                Correct Answer
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Explanation / Solution Accordion */}
                    {q.explanation && (
                      <div className="q_solution_wrapper">
                        <button
                          type="button"
                          className="toggle_solution_btn"
                          onClick={() => toggleSolution(q.id)}
                        >
                          <span>
                            {isSolutionExpanded ? "Hide" : "View"} Step-by-Step Solution
                          </span>
                          <svg
                            className={`chevron ${isSolutionExpanded ? "rotate" : ""}`}
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.5"
                          >
                            <polyline points="6 9 12 15 18 9" />
                          </svg>
                        </button>

                        {isSolutionExpanded && (
                          <div className="solution_body">
                            <div className="sol_heading">
                              <svg
                                width="14"
                                height="14"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="#10B981"
                                strokeWidth="2.5"
                              >
                                <circle cx="12" cy="12" r="10" />
                                <line x1="12" y1="16" x2="12" y2="12" />
                                <line x1="12" y1="8" x2="12.01" y2="8" />
                              </svg>
                              Explanation:
                            </div>
                            <div className="sol_content">
                              <LatexRenderer text={q.explanation} highlight={searchQuery} />
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>

          {/* Pagination Controls */}
          {totalQuestionsCount > 0 && (
            <div className="qb_pagination_container">
              <div className="pagination_page_size_selector">
                <span className="size_label">Questions per page:</span>
                <div className="size_select_wrap">
                  <select
                    value={pageSize}
                    onChange={(e) => handlePageSizeChange(e.target.value)}
                  >
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                  </select>
                  <svg
                    className="select_arrow"
                    width="10"
                    height="10"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                  >
                    <polyline points="6 9 12 15 18 9"></polyline>
                  </svg>
                </div>
              </div>

              <div className="pagination_info_text">
                <span className="showing_label">Showing</span>
                <span className="range_highlight">
                  {startIndex} – {endIndex}
                </span>
                <span className="questions_label">questions</span>
              </div>

              <div className="pagination_nav">
                <button
                  type="button"
                  className="page_nav_btn"
                  disabled={currentPage === 1 || loadingQuestions}
                  onClick={() => handlePageChange(currentPage - 1)}
                  title="Previous Page"
                >
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                  >
                    <polyline points="15 18 9 12 15 6" />
                  </svg>
                  Previous
                </button>

                <div className="page_number_buttons">
                  {getPageNumbers(currentPage, totalPages).map((p, idx) =>
                    p === "..." ? (
                      <span key={`ellipsis-${idx}`} className="page_ellipsis">
                        …
                      </span>
                    ) : (
                      <button
                        key={`page-${p}`}
                        type="button"
                        className={`page_num_btn ${currentPage === p ? "active" : ""}`}
                        disabled={loadingQuestions}
                        onClick={() => handlePageChange(p)}
                      >
                        {p}
                      </button>
                    )
                  )}
                </div>

                <button
                  type="button"
                  className="page_nav_btn"
                  disabled={currentPage === totalPages || totalPages === 0 || loadingQuestions}
                  onClick={() => handlePageChange(currentPage + 1)}
                  title="Next Page"
                >
                  Next
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                  >
                    <polyline points="9 18 15 12 9 6" />
                  </svg>
                </button>
              </div>
            </div>
          )}

          {/* Floating Action Bar when questions are selected */}
          {selectedQuestionIds.size > 0 && (
            <div className="qb_floating_action_bar">
              <div className="bar_left">
                <span className="selection_counter">
                  {selectedQuestionIds.size} Question{selectedQuestionIds.size > 1 ? "s" : ""}{" "}
                  Selected
                </span>
              </div>
              <div className="bar_right">
                <button
                  type="button"
                  className="bar_secondary_btn"
                  onClick={() => handleSelectAllVisible()}
                >
                  Clear Selection
                </button>
                <button
                  type="button"
                  className="bar_primary_btn"
                  onClick={() => {
                    alert(`${selectedQuestionIds.size} questions added to paper creation flow!`);
                  }}
                >
                  Create Question Paper ({selectedQuestionIds.size}) →
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ────────────────────────────────────────────────────────────
          MODE 3: CREATED QUESTION PAPERS LIST (View Created Papers)
          ──────────────────────────────────────────────────────────── */}
      {viewMode === "papers" && (
        <div className="qb_papers_view_section">
          <div className="qb_questions_top_bar">
            <button
              type="button"
              className="back_to_cards_btn"
              onClick={handleBackToCards}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
              >
                <line x1="19" y1="12" x2="5" y2="12" />
                <polyline points="12 19 5 12 12 5" />
              </svg>
              Back to Creation Options
            </button>
          </div>

          <div className="qb_section_banner">
            <div className="banner_info">
              <span className="badge_pill blue">CREATED QUESTION PAPERS</span>
              <h2>Your Created Question Papers</h2>
              <p>Review, preview, and export question papers that have been generated.</p>
            </div>
          </div>

          {/* Papers Grid */}
          <div className="qb_papers_grid">
            {loadingPapers ? (
              <div className="empty_state loading_ai_state">
                <div className="empty_icon">⚡</div>
                <h3>Fetching Question Papers...</h3>
                <p>Loading your created papers from database.</p>
              </div>
            ) : filteredPapers.length === 0 ? (
              <div className="empty_state">
                <div className="empty_icon">📄</div>
                <h3>No Created Question Papers Found</h3>
                <p>You haven't created any question papers yet or none match your criteria.</p>
              </div>
            ) : (
              filteredPapers.map((paper) => (
                <div key={paper.id} className="paper_card">
                  <div className="paper_card_top">
                    <span className="paper_exam_badge">{paper.exam}</span>
                    <span className="paper_status_badge">{paper.status}</span>
                  </div>

                  <h3>{paper.title}</h3>
                  <p className="paper_creator">
                    Generated via <strong>{paper.created_by}</strong>
                  </p>

                  <div className="paper_meta_row">
                    <div className="meta_item">
                      <span className="meta_label">Class</span>
                      <span className="meta_val">{paper.class_name || "Class 11"}</span>
                    </div>
                    <div className="meta_item">
                      <span className="meta_label">Questions</span>
                      <span className="meta_val">{paper.total_questions} Qs</span>
                    </div>
                    <div className="meta_item">
                      <span className="meta_label">Total Marks</span>
                      <span className="meta_val">{paper.total_marks} Marks</span>
                    </div>
                  </div>

                  <div className="paper_actions">
                    <button
                      type="button"
                      className="preview_paper_btn"
                      onClick={() => handleOpenPaperPreview(paper)}
                    >
                      Preview Paper
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Preview Paper Modal */}
          {previewModalOpen && previewPaper && (
            <div
              className="qb_preview_modal_overlay"
              onClick={() => setPreviewModalOpen(false)}
            >
              <div
                className="qb_preview_modal_content"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="modal_header">
                  <div>
                    <h3>{previewPaper.title}</h3>
                    <p>
                      {previewPaper.exam} • {previewPaper.subject} • {previewPaper.total_questions} Questions • {previewPaper.duration_minutes} Mins
                    </p>
                  </div>
                  <button
                    type="button"
                    className="close_btn"
                    onClick={() => setPreviewModalOpen(false)}
                  >
                    ×
                  </button>
                </div>

                <div className="modal_body">
                  <div className="mock_paper_notice">
                    This is a preview of the question paper. All questions and layout are synchronized with the {previewPaper.exam} curriculum standards.
                  </div>
                </div>

                <div className="modal_footer">
                  <button
                    type="button"
                    className="modal_close_action"
                    onClick={() => setPreviewModalOpen(false)}
                  >
                    Close Preview
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ────────────────────────────────────────────────────────────
          IMAGE ZOOM LIGHTBOX MODAL
          ──────────────────────────────────────────────────────────── */}
      {zoomModalImage && (
        <div
          className="qb_image_zoom_overlay"
          onClick={() => {
            setZoomModalImage(null);
            setZoomScale(1.5);
          }}
        >
          <div
            className="qb_image_zoom_modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="zoom_modal_header">
              <span className="zoom_modal_title">
                {zoomModalImage.title || "Image Preview"}
              </span>
              <div className="zoom_controls_group">
                <button
                  type="button"
                  className="zoom_ctrl_btn"
                  title="Zoom Out"
                  onClick={() => setZoomScale((prev) => Math.max(0.5, Number((prev - 0.25).toFixed(2))))}
                >
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                  >
                    <circle cx="11" cy="11" r="8"></circle>
                    <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                    <line x1="8" y1="11" x2="14" y2="11"></line>
                  </svg>
                </button>
                <span className="zoom_scale_indicator">
                  {Math.round(zoomScale * 100)}%
                </span>
                <button
                  type="button"
                  className="zoom_ctrl_btn"
                  title="Zoom In"
                  onClick={() => setZoomScale((prev) => Math.min(3.5, Number((prev + 0.25).toFixed(2))))}
                >
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                  >
                    <circle cx="11" cy="11" r="8"></circle>
                    <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                    <line x1="11" y1="8" x2="11" y2="14"></line>
                    <line x1="8" y1="11" x2="14" y2="11"></line>
                  </svg>
                </button>
                <button
                  type="button"
                  className="zoom_ctrl_btn reset_btn"
                  title="Reset Zoom"
                  onClick={() => setZoomScale(1.5)}
                >
                  Reset
                </button>
                <button
                  type="button"
                  className="zoom_close_btn"
                  title="Close (Esc)"
                  onClick={() => {
                    setZoomModalImage(null);
                    setZoomScale(1.5);
                  }}
                >
                  ×
                </button>
              </div>
            </div>
            <div className="zoom_modal_body">
              <div className="zoom_img_scroll_container">
                <img
                  key={zoomModalImage.src}
                  src={zoomModalImage.src}
                  alt={zoomModalImage.title || "Zoomed diagram"}
                  className="zoom_preview_img"
                  style={{ transform: `scale(${zoomScale})` }}
                  onError={handleImageError}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Edit Question Modal ── */}
      {editingQuestion && (
        <div
          className="eq_modal_overlay"
          onClick={() => setEditingQuestion(null)}
        >
          <div
            className="eq_modal_box"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="eq_modal_header">
              <div>
                <h3 className="eq_modal_title">Edit Question</h3>
                <p className="eq_modal_subtitle">Review and correct the extracted text</p>
              </div>
              <svg
                className="eq_modal_close"
                onClick={() => setEditingQuestion(null)}
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#9CA3AF"
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </div>

            {/* Question Text */}
            <div className="eq_section">
              <label className="eq_section_label">Question Text</label>
              <textarea
                value={editPromptText}
                onChange={(e) => setEditPromptText(e.target.value)}
                rows={3}
                className="eq_textarea"
                placeholder="Enter question text..."
              />

              {/* Live Rendered LaTeX Preview */}
              {editPromptText && containsLatexPattern(editPromptText) && (
                <div className="eq_live_preview_card">
                  <div className="eq_preview_header">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#4F46E5" strokeWidth="2.5">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                    <span>Live Formula Preview</span>
                  </div>
                  <div className="eq_preview_body">
                    <LatexRenderer text={editPromptText} />
                  </div>
                </div>
              )}

              {/* Stem Image Preview */}
              {editStemImage && (
                <div className="stem_image_preview_box">
                  <img src={editStemImage} alt="Question Diagram" className="stem_preview_img" />
                  <button
                    type="button"
                    className="image_delete_badge"
                    onClick={(e) => handleStemImageDelete(e)}
                    title="Remove image"
                    disabled={uploadingTarget === "stem"}
                  >
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="18" y1="6" x2="6" y2="18" />
                      <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                  </button>
                </div>
              )}

              {/* Upload Question Image Button */}
              <div className="upload_btn_row">
                <input
                  type="file"
                  accept="image/*"
                  id="stem-image-upload-qb"
                  style={{ display: "none" }}
                  onChange={handleStemImageUpload}
                />
                <label htmlFor="stem-image-upload-qb" className="eq_upload_btn">
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#612EF7" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="17 8 12 3 7 8" />
                    <line x1="12" y1="3" x2="12" y2="15" />
                  </svg>
                  <span>{uploadingTarget === "stem" ? "Uploading..." : "Upload Image"}</span>
                </label>
              </div>
            </div>

            {/* Answer Options or Numerical Answer */}
            {(() => {
              const isMcqType = (editingQuestion.type || "").toLowerCase().includes("mcq") || (editingQuestion.options && editingQuestion.options.length > 0);
              const hasTextOptions = Boolean(editOptionA || editOptionB || editOptionC || editOptionD || editOptionE);
              const hasImgOptions = Boolean(editOptionImages && (editOptionImages.A || editOptionImages.B || editOptionImages.C || editOptionImages.D || editOptionImages.E));
              const showMcqOptions = isMcqType || hasTextOptions || hasImgOptions;

              if (!showMcqOptions) {
                return (
                  <div className="eq_section" style={{ marginTop: "16px" }}>
                    <label className="eq_section_label">Correct Answer</label>
                    <input
                      type="text"
                      value={editCorrectAnswer}
                      onChange={(e) => setEditCorrectAnswer(e.target.value)}
                      className="option_text_input"
                      style={{ width: "100%", height: "40px", padding: "0 14px", borderRadius: "8px", border: "1px solid #D1D5DB", outline: "none", fontWeight: 600, color: "#111827", background: "#fff" }}
                      placeholder="Enter correct answer (e.g. 5)"
                    />
                  </div>
                );
              }

              return (
                <div className="eq_section" style={{ marginTop: "20px" }}>
                  <label className="eq_section_label">Answer Options</label>
                  <div className="options_edit_list">
                    {[
                      { key: "A", val: editOptionA, setter: setEditOptionA },
                      { key: "B", val: editOptionB, setter: setEditOptionB },
                      { key: "C", val: editOptionC, setter: setEditOptionC },
                      { key: "D", val: editOptionD, setter: setEditOptionD },
                      ...(editingQuestion?.optionE || editingQuestion?.optionImages?.E || editOptionE || editOptionImages?.E || editingQuestion?.options?.some((o) => o.id === "E")
                        ? [{ key: "E", val: editOptionE, setter: setEditOptionE }]
                        : []),
                    ].map(({ key, val, setter }) => {
                      const optImgUrl = editOptionImages?.[key];
                      const inputId = `opt-${key.toLowerCase()}-image-upload-qb`;
                      return (
                        <div key={key} className="option_edit_wrapper">
                          <div className="option_edit_row">
                            <div className="option_letter_badge">{key}</div>
                            <input
                              type="text"
                              value={val}
                              onChange={(e) => setter(e.target.value)}
                              className="option_text_input"
                              placeholder={`Option ${key}`}
                            />
                            {optImgUrl && (
                              <div className="option_image_thumb_box">
                                <img src={optImgUrl} alt={`Option ${key}`} className="option_thumb_img" />
                                <button
                                  type="button"
                                  className="image_delete_badge"
                                  onClick={(e) => handleOptionImageDelete(key, e)}
                                  title="Remove image"
                                  disabled={uploadingTarget === key}
                                >
                                  <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                    <line x1="18" y1="6" x2="6" y2="18" />
                                    <line x1="6" y1="6" x2="18" y2="18" />
                                  </svg>
                                </button>
                              </div>
                            )}
                            <input
                              type="file"
                              accept="image/*"
                              id={inputId}
                              style={{ display: "none" }}
                              onChange={(e) => handleOptionImageUpload(key, e)}
                            />
                            <label htmlFor={inputId} className="option_upload_btn">
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#612EF7" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                <polyline points="17 8 12 3 7 8" />
                                <line x1="12" y1="3" x2="12" y2="15" />
                              </svg>
                              <span>{uploadingTarget === key ? "Uploading..." : "Image"}</span>
                            </label>
                          </div>
                          {val && containsLatexPattern(val) && (
                            <div className="option_live_preview">
                              <span className="option_preview_tag">Preview:</span>
                              <div className="option_preview_math">
                                <LatexRenderer text={val} inline={true} />
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  <div className="eq_section" style={{ marginTop: "16px" }}>
                    <label className="eq_section_label">Correct Answer Choice</label>
                    <select
                      value={editCorrectAnswer}
                      onChange={(e) => setEditCorrectAnswer(e.target.value)}
                      className="eq_select"
                      style={{
                        width: "100%",
                        height: "40px",
                        padding: "0 14px",
                        borderRadius: "8px",
                        border: "1px solid #D1D5DB",
                        fontWeight: 600,
                        color: "#111827",
                        outline: "none",
                        background: "#fff",
                        cursor: "pointer",
                      }}
                    >
                      <option value="A">Option A</option>
                      <option value="B">Option B</option>
                      <option value="C">Option C</option>
                      <option value="D">Option D</option>
                      {(editingQuestion?.optionE || editingQuestion?.optionImages?.E || editOptionE || editOptionImages?.E || editCorrectAnswer === "E" || editingQuestion?.options?.some((o) => o.id === "E")) && (
                        <option value="E">Option E</option>
                      )}
                    </select>
                  </div>
                </div>
              );
            })()}

            {/* Modal Footer Actions */}
            <div className="eq_modal_footer">
              <button
                type="button"
                className="eq_cancel_btn"
                onClick={() => setEditingQuestion(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="eq_save_btn"
                onClick={handleEditSave}
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Choose Questions Popup Modal */}
      {showChooseModal && (
        <ChoosePopup
          onClose={() => setShowChooseModal(false)}
          onProceed={handleProceedChoose}
        />
      )}

      {/* Import Questions Popup Modal */}
      {showImportModal && (
        <ImportPopup
          onClose={() => setShowImportModal(false)}
          onUploadSuccess={handleUploadSuccess}
        />
      )}

      {/* Imported Questions Modal (Post-upload workflow) */}
      {showImportedQuestions && (
        <ImportedQuestionsModal
          testName={activeTestName}
          exam={selectedExam || "NEET"}
          subject={"Biology"}
          level={activeLevel || ""}
          isImportFlow={isImportFlow}
          onClose={() => setShowImportedQuestions(false)}
          onComplete={handleCompleteQuestionPaper}
        />
      )}

      {/* Success Modal */}
      {showSuccess && (
        <SuccessModal
          testName={activeTestName}
          onClose={() => setShowSuccess(false)}
          onGoToQuestionPapers={() => {
            setShowSuccess(false);
            if (handleOpenPapers) {
              handleOpenPapers();
            }
          }}
        />
      )}
    </div>
  );
};

export default QuestionBankView;
